import { useQueryClient } from '@tanstack/react-query';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { Modal, useToast } from '../../componentes/ui';
import { api, patch, post } from '../../lib/api';
import { chaves, useRespostasRapidas } from '../../lib/consultas';

type Resposta = { id: string; atalho: string; texto: string };

export function RespostasRapidas() {
  const lista = useRespostasRapidas().data ?? [];
  const [editando, setEditando] = useState<Resposta | 'nova' | null>(null);
  const [apagando, setApagando] = useState<Resposta | null>(null);

  return (
    <div className="config-cartao largo">
      <header className="com-acao">
        <div>
          <h2>Respostas rápidas</h2>
          <p>
            No chat, digite <b className="mono">/</b> e o atalho pra usar. Ex:{' '}
            <b className="mono">/pix</b>.
          </p>
        </div>
        <button className="btn primario" onClick={() => setEditando('nova')}>
          <Plus /> Nova resposta
        </button>
      </header>
      {lista.length === 0 ? (
        <p className="nota-sec">Nenhuma resposta rápida ainda.</p>
      ) : (
        <ul className="lista-config">
          {lista.map((r) => (
            <li key={r.id}>
              <b className="mono">/{r.atalho}</b>
              <span className="lista-texto">{r.texto}</span>
              <span className="lista-acoes">
                <button
                  className="icone-btn sem-borda"
                  aria-label={`Editar /${r.atalho}`}
                  onClick={() => setEditando(r)}
                >
                  <Pencil />
                </button>
                <button
                  className="icone-btn sem-borda perigo"
                  aria-label={`Apagar /${r.atalho}`}
                  onClick={() => setApagando(r)}
                >
                  <Trash2 />
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}
      {editando && (
        <ModalResposta
          atual={editando === 'nova' ? null : editando}
          fechar={() => setEditando(null)}
        />
      )}
      {apagando && <ModalApagarResposta r={apagando} fechar={() => setApagando(null)} />}
    </div>
  );
}

function ModalResposta({ atual, fechar }: { atual: Resposta | null; fechar: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [erro, setErro] = useState<string | null>(null);

  async function salvar(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const d = Object.fromEntries(new FormData(e.currentTarget)) as Record<string, string>;
    const corpo = { atalho: d.atalho!.replace(/^\//, ''), texto: d.texto };
    try {
      if (atual) await patch(`/api/respostas-rapidas/${atual.id}`, corpo);
      else await post('/api/respostas-rapidas', corpo);
      qc.invalidateQueries({ queryKey: chaves.respostas });
      toast(atual ? 'Resposta atualizada' : 'Resposta criada');
      fechar();
    } catch (err) {
      setErro((err as Error).message);
    }
  }

  return (
    <Modal titulo={atual ? `Editar /${atual.atalho}` : 'Nova resposta rápida'} aoFechar={fechar}>
      <form onSubmit={salvar} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <label className="campo">
          <span>Atalho</span>
          <input
            id="rr-atalho"
            name="atalho"
            className="input mono"
            defaultValue={atual?.atalho}
            placeholder="pix"
            required
            maxLength={30}
          />
          <small>Só letras, números e hífen. É o que vem depois da barra.</small>
        </label>
        <label className="campo">
          <span>Texto</span>
          <textarea
            id="rr-texto"
            name="texto"
            className="input"
            rows={4}
            defaultValue={atual?.texto}
            required
            maxLength={1000}
          />
        </label>
        {erro && <div className="erro-form">{erro}</div>}
        <div className="acoes">
          <button type="button" className="btn" onClick={fechar}>
            Cancelar
          </button>
          <button className="btn primario">Salvar</button>
        </div>
      </form>
    </Modal>
  );
}

function ModalApagarResposta({ r, fechar }: { r: Resposta; fechar: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  async function apagar() {
    try {
      await api(`/api/respostas-rapidas/${r.id}`, { metodo: 'DELETE' });
      qc.invalidateQueries({ queryKey: chaves.respostas });
      toast('Resposta apagada');
      fechar();
    } catch (e) {
      toast((e as Error).message, 'erro');
    }
  }
  return (
    <Modal titulo="Apagar resposta rápida" aoFechar={fechar}>
      <p style={{ margin: 0 }}>
        Apagar <b className="mono">/{r.atalho}</b>?
      </p>
      <div className="acoes">
        <button className="btn" onClick={fechar}>
          Cancelar
        </button>
        <button className="btn perigo-cheio" onClick={() => void apagar()}>
          Apagar
        </button>
      </div>
    </Modal>
  );
}
